import { useCallback, useEffect, useState } from "react";

import { api, ApiError, type ReceivedTeamInvitation } from "../lib/api";

// Invitación a un equipo (Eduardo 2026-10-20, maqueta Equipo-Invitar C):
// la ve en su Inicio quien ya usa Torneos123 y fue invitado por correo.
// Entra a los torneos del organizador SOLO al aceptar.

function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.length > 1 ? parts[0][0] + parts[parts.length - 1][0] : (parts[0] ?? "?").slice(0, 2);
  return letters.toUpperCase();
}

export default function TeamInvitationsCard({ token, onAccepted }: { token: string | null; onAccepted?: () => void }) {
  const [invitations, setInvitations] = useState<ReceivedTeamInvitation[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!token) return;
    try {
      const response = await api.tournaments.listReceivedTeamInvitations(token);
      setInvitations(response.data.invitations);
    } catch {
      setInvitations([]);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  async function respond(invitation: ReceivedTeamInvitation, accept: boolean) {
    if (!token) return;
    setBusyId(invitation.id);
    setError(null);
    setMessage(null);
    try {
      if (accept) {
        await api.tournaments.acceptTeamInvitation(token, invitation.id);
        setMessage(`Ya estás en el equipo de ${invitation.organizer_name}. Sus torneos aparecen en tu menú.`);
        onAccepted?.();
      } else {
        await api.tournaments.declineTeamInvitation(token, invitation.id);
        setMessage(`Rechazaste la invitación de ${invitation.organizer_name}.`);
      }
      setInvitations((current) => current.filter((item) => item.id !== invitation.id));
    } catch (err) {
      setError(err instanceof ApiError ? err.message || "No se pudo responder la invitación." : "Error de red. Intenta otra vez.");
      await load();
    } finally {
      setBusyId(null);
    }
  }

  if (invitations.length === 0 && !message && !error) return null;

  return (
    <section className="team-invite-cards" aria-label="Invitaciones a un equipo">
      {error && <p role="alert" className="form-error">{error}</p>}
      {message && <p className="form-message">{message}</p>}
      {invitations.map((invitation) => {
        const busy = busyId === invitation.id;
        const count = invitation.tournaments_count;
        return (
          <article key={invitation.id} className="team-invite-card">
            <span className="team-invite-card__avatar" aria-hidden="true">{initialsOf(invitation.organizer_name)}</span>
            <div className="team-invite-card__body">
              <span className="team-invite-card__eyebrow">Invitación a un equipo</span>
              <h2 className="team-invite-card__title">
                {invitation.organizer_name} te invitó a su equipo como {invitation.profile_label} en{" "}
                {count === 1 ? "1 torneo" : `${count} torneos`}
              </h2>
              {invitation.tournament_names.length > 0 ? (
                <ul className="team-invite-card__chips">
                  {invitation.tournament_names.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              ) : null}
              <p className="team-invite-card__note">
                Si aceptas, verás esos torneos en tu menú. Tu cuenta, tus torneos y tu contraseña siguen siendo solo tuyos.
              </p>
              <div className="team-invite-card__actions">
                <button type="button" className="adm-players__btn adm-players__btn--primary" onClick={() => void respond(invitation, true)} disabled={busy}>
                  {busy ? "Un momento…" : "Aceptar"}
                </button>
                <button type="button" className="adm-players__btn adm-players__btn--secondary" onClick={() => void respond(invitation, false)} disabled={busy}>
                  Rechazar
                </button>
              </div>
            </div>
          </article>
        );
      })}
    </section>
  );
}
